import type { Entity } from '@backstage/catalog-model';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { EntityHeaderBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { isStackEntity } from '@internal/plugin-stack-common';

/**
 * The Stack header replaces the stock one for Stack entities only.
 *
 * The filter is guarded rather than handed `isStackEntity` directly. The entity
 * page picks its header with `h.filter(entity)` while the entity may still be
 * loading and `entity` is therefore `undefined` (`@backstage/plugin-catalog`
 * `alpha/pages.esm.js`, the `headers.find` call - the sibling `contextMenuItems`
 * line does guard for it). `isStackEntity` dereferences `entity.apiVersion`, so
 * an unguarded filter throws on every first render and takes the whole page
 * down with it. While the entity is undefined this returns false and the stock
 * header, which has no filter, renders instead.
 */
const stackEntityHeader = EntityHeaderBlueprint.make({
  name: 'stack',
  params: {
    filter: (entity: Entity | undefined) => !!entity && isStackEntity(entity),
    loader: () =>
      import('./StackEntityHeader').then(m => <m.StackEntityHeader />),
  },
});

/**
 * The order the mockup gives a Stack's tabs: what the thing is, then how to get
 * into it, then what it is doing. Logs are part of the Grafana Monitoring tab;
 * the legacy dedicated Logs tab is deliberately no longer registered.
 *
 * The platform's Stack tabs are listed. Every one of them is
 * contributed by a different plugin, and the entity page renders ungrouped tabs
 * in the order its `contents` input arrives in - which is the order of the
 * `features` array in `App.tsx` (the title sort in `EntityTabsList` only
 * applies inside a declared group, and none of these declare one). That makes
 * the live order Console, Details, Resource Graph, Monitoring, Storage
 * Lifecycle, and no rearrangement of `features` can fix it: Details and
 * Resource Graph both come from `stack-entity` and would always end up
 * adjacent.
 */
const STACK_TAB_ORDER = [
  'entity-content:stack-entity/stack-details',
  'entity-content:remote-access/remote-access',
  'entity-content:storage-lifecycle/storage-lifecycle',
  'entity-content:stack-grafana/stack-grafana-monitoring',
  'entity-content:stack-entity/stack-graph',
  'entity-content:policy/stack-policy-evaluations',
];

/**
 * Sorts the Stack tabs into `STACK_TAB_ORDER` without moving anything else.
 *
 * A permutation in place rather than a sort of the whole list: the same input
 * carries the tabs of every other kind in the catalog - Component, API, the
 * ApplicationGroup pages - and those keep whatever order their plugins were
 * registered in. Only the positions these Stack tabs already occupy are
 * reused, so a tab this app does not know about cannot be displaced by one it
 * does.
 */
const orderStackTabs = <T extends { node: { spec: { id: string } } }>(
  contents: T[],
): T[] => {
  const slots: number[] = [];
  contents.forEach((content, index) => {
    if (STACK_TAB_ORDER.includes(content.node.spec.id)) {
      slots.push(index);
    }
  });
  if (slots.length < 2) {
    return contents;
  }

  const ordered = slots
    .map(index => contents[index])
    .sort(
      (a, b) =>
        STACK_TAB_ORDER.indexOf(a.node.spec.id) -
        STACK_TAB_ORDER.indexOf(b.node.spec.id),
    );

  const result = [...contents];
  slots.forEach((slot, position) => {
    result[slot] = ordered[position];
  });
  return result;
};

/**
 * Reorders the entity page's tabs by forwarding a permuted `contents` input.
 *
 * Every input has to be forwarded, not just the one being changed:
 * `resolveInputOverrides` walks the declared inputs and throws for any
 * non-singleton input whose override is not an array, so omitting `pages`,
 * `headers` or `contextMenuItems` here would fail the page at render time
 * rather than silently pass them through.
 */
const entityPage = catalogPlugin.getExtension('page:catalog/entity').override({
  factory(originalFactory, { inputs }) {
    return originalFactory({
      inputs: {
        pages: inputs.pages,
        headers: inputs.headers,
        contextMenuItems: inputs.contextMenuItems,
        contents: orderStackTabs(inputs.contents),
      },
    });
  },
});

export const entityModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [stackEntityHeader, entityPage],
});
